import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { AttributionScope } from "@redotech/redo-attribution-common/attribution-scope";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import {
  SendEmailStep,
  SendSmsStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageIcon from "@redotech/redo-icons/message-check-square.svg";
import {
  ATTRIBUTION_ERROR_TEXT,
  attributionDisplay,
} from "@redotech/redo-merchant-app-common/attribution/attribution-data";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { formatAttributedRevenue } from "@redotech/redo-merchant-app-common/marketing/attributed-marketing-display";
import { useAttribution } from "@redotech/redo-merchant-app-common/marketing/use-attribution";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  useAutomationAttributionWindow,
  useOptionalAutomationMetricsDateRange,
} from "@redotech/redo-merchant-marketing-app/automations/automation-metrics-date-range-context";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { ReactNode, useContext, useMemo } from "react";
import { PartialWithRequiredType } from "../steps";
import { AutomationContext } from "../steps/context";
import { AnalyticsBlock } from "./analytics-block";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface SendMessageNodeData {
  isEmail: boolean;
  deleteElements: (id: string) => void;
  automationId: string;
  templateId: string | undefined;
  originTemplateId: string | undefined;
  step: PartialWithRequiredType<SendEmailStep | SendSmsStep>;
}

export function SendMessageNode(data: {
  id: string;
  data: SendMessageNodeData;
  selected?: boolean;
}) {
  const { t } = useTranslation();
  const marketingRpcClient = useContext(RedoMarketingRpcClientContext);
  const automationContext = useContext(AutomationContext);

  const sendMessageData = useMemo(() => {
    return data.data;
  }, [data.data]);

  const existingTemplate = useMemo(() => {
    if (!automationContext) {
      return null;
    }

    // If there is an origin template id, look for a customized or origin template.
    if (sendMessageData.originTemplateId) {
      const customizedTemplate = automationContext.emailTemplates.find(
        (template) =>
          template.originTemplateId === sendMessageData.originTemplateId,
      );
      if (customizedTemplate) {
        return customizedTemplate;
      }
      const originTemplate = automationContext.originEmailTemplates.find(
        (template) =>
          template._id.toString() === sendMessageData.originTemplateId,
      );
      if (originTemplate) {
        return originTemplate;
      }
    }

    const templateId =
      sendMessageData.templateId || sendMessageData.originTemplateId;
    if (!templateId) {
      return null;
    }

    if (sendMessageData.isEmail) {
      const template = automationContext.emailTemplates.find(
        (template) => template._id.toString() === templateId,
      );
      return template ?? null;
    } else {
      const template = automationContext.smsTemplates.find(
        (template) => template._id.toString() === templateId,
      );

      return template ?? null;
    }
  }, [
    sendMessageData.templateId,
    sendMessageData.originTemplateId,
    sendMessageData.isEmail,
    automationContext,
  ]);

  const dateRangeCtx = useOptionalAutomationMetricsDateRange();
  const metricsStart = dateRangeCtx?.dateRange[0].toString();
  const metricsEnd = dateRangeCtx?.dateRange[1].toString();

  const metricsLoad = useLoad(async () => {
    if (!marketingRpcClient) {
      return;
    }
    const metrics = await marketingRpcClient.getOutreachAnalytics({
      groupBy: {
        automation: sendMessageData.automationId,
        automationStepId: data.id,
      },
      metricsStart,
      metricsEnd,
    });
    return metrics;
  }, [
    marketingRpcClient,
    sendMessageData.automationId,
    sendMessageData.templateId,
    sendMessageData.isEmail,
    metricsStart,
    metricsEnd,
  ]);

  const metrics = metricsLoad.value;

  const team = useRequiredContext(TeamContext);
  const attributionWindow = useAutomationAttributionWindow();
  // Every step node asks for the same grouped result, so the query layer serves
  // them all from one request.
  const attributionLoad = useAttribution({
    groupBy: AttributionScope.AUTOMATION_STEP,
    window: attributionWindow,
  });
  const legacyRevenue = metrics?.upsellRevenue ?? 0;
  const legacyText =
    legacyRevenue !== 0
      ? `$${legacyRevenue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "-";
  // Without a date range there is no window to attribute over, so the step
  // keeps its stored total rather than querying all of history.
  const revenueDisplay = attributionWindow
    ? attributionDisplay({
        load: attributionLoad,
        select: (attribution) => attribution[data.id],
        legacy: legacyText,
        format: (metric) =>
          formatAttributedRevenue(metric, getShopCurrencyWithDefault({ team })),
      })
    : { pending: false, value: legacyText };

  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        disabled={sendMessageData.step.disabled}
        icon={
          <RedoFeaturedIcon
            color="success"
            Icon={sendMessageData.isEmail ? MailIcon : MessageIcon}
            size="xs"
          />
        }
        onDelete={() => sendMessageData.deleteElements(data.id)}
        selected={data.selected}
        step={sendMessageData.step}
        title={sendMessageData.isEmail ? t("Send email") : t("Send SMS")}
      >
        <Flex className={NodeCSS.fullWidth} dir="column" justify="flex-start">
          <Text fontSize="xxs">{t("Template")}</Text>
          {existingTemplate ? (
            <Text className={NodeCSS.valueText}>
              {existingTemplate.name || "-"}
            </Text>
          ) : (
            <Text className={NodeCSS.placeholderText}>
              {(sendMessageData.templateId ||
                sendMessageData.originTemplateId) &&
              existingTemplate !== null
                ? t("Loading...")
                : t("Needs configuration")}
            </Text>
          )}
        </Flex>
      </BaseNode>
      <AnalyticsBlock
        clicks={metrics?.uniqueClicks ?? 0}
        opened={metrics?.uniqueOpens ?? 0}
        revenue={attributedRevenueContent(revenueDisplay)}
        sent={metrics?.sends ?? 0}
        skippedDueToSkipConditions={metrics?.skippedDueToSkipConditions ?? 0}
        skippedDueToUnsubscribe={metrics?.skippedDueToUnsubscribe ?? 0}
      />
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}

function attributedRevenueContent(load: LoadState<string>): ReactNode {
  if (load.pending) {
    return <SkeletonText length={5} />;
  }
  return load.value ?? ATTRIBUTION_ERROR_TEXT;
}
